import React, {useEffect, useState} from "react";
import {useFirstPageCheck, useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import {Button, PanelHeader, PanelHeaderBack, Spacing, Spinner} from "@vkontakte/vkui";
import BackgroundLetters from "../../components/Background/Background";
import {fetchActiveMissions, claimMissionReward} from "../../services/missions";
import {useSnackbar} from "../../context/SnackbarContext";
import BonusStore from "../../store/bonus";
import GameStore from "../../store/game";
import {Icon28TargetOutline} from "@vkontakte/icons";
import {getSubscriptionStatus} from "../../services/subscription";
import {SUBSCRIBE_MISSIONS_MODAL} from "../../routing/routing";
import {getTypeBadgeClasses} from "../../utils/badges";

const BADGE_CLASSES = "inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide bg-emerald-700 text-white border border-emerald-800";
const BUTTON_CLASSES = "bg-yellow-400 hover:bg-yellow-500 text-gray-800 border-yellow-400";
const TIME_TARGET_METRIC = "time_played_seconds";
const SUBSCRIPTION_MODAL_SHOWN_KEY = "subscriptionModalShownAfterMission";

const hintLabels = {
    time_boost: "Time Boost",
    letters_reroll: "Letter Shuffle"
};

// Форматирует оставшееся время в человекочитаемый формат
function formatTimeLeft(seconds) {
    if (seconds <= 0) {
        return "expired";
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainder = seconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
    }
    if (minutes > 0) {
        return `${minutes}m ${remainder.toString().padStart(2, "0")}s`;
    }
    return `${remainder}s`;
}

// Нормализует объект миссии, чтобы избежать undefined
function normalizeMission(mission) {
    return {
        ...mission,
        title: mission.title ?? "",
        description: mission.description ?? "",
        // Оставляем секунды как есть от сервера (в московском времени).
        timeLeftSeconds: Math.max(Math.floor(mission.timeLeftSeconds ?? 0), 0),
        progress: Math.max(mission.progress ?? 0, 0),
        targetValue: Math.max(mission.targetValue ?? 0, 0),
        targetMetric: mission.targetMetric ?? "",
        rewardPreview: mission.rewardPreview || {}
    };
}

// Возвращает целое число минут для прогресса по времени
function toWholeMinutes(value) {
    return Math.floor(value / 60);
}

// Форматирует текст прогресса в зависимости от метрики
function getMissionProgressText(mission) {
    if (mission.targetMetric !== TIME_TARGET_METRIC) {
        return `${mission.progress}/${mission.targetValue}`;
    }
    const progressMinutes = toWholeMinutes(mission.progress);
    const targetMinutes = toWholeMinutes(mission.targetValue);
    return `${progressMinutes}/${targetMinutes} min`;
}

// Уменьшает таймер миссии и отмечает, если она истекла
function updateTime(mission) {
    if (mission.timeLeftSeconds <= 0) {
        if (mission.status === "expired") {
            return mission;
        }
        // Не скрываем выполненные/полученные миссии, даже если время истекло.
        if (mission.status === "completed" || mission.status === "claimed") {
            return {...mission, timeLeftSeconds: 0};
        }
        return {...mission, timeLeftSeconds: 0, status: "expired"};
    }
    return {...mission, timeLeftSeconds: mission.timeLeftSeconds - 1};
}

// Возвращает общий таймер для группы миссий, чтобы не дублировать счётчики в карточках.
function getGroupTimeLeftSeconds(missions) {
    return missions.reduce((maxValue, mission) => Math.max(maxValue, mission.timeLeftSeconds ?? 0), 0);
}

export default function MissionPanel() {
    const [missions, setMissions] = useState({daily: [], weekly: []});
    const routeNavigator = useRouteNavigator();
    const isFirstPage = useFirstPageCheck();
    const handleBackButton = () => (isFirstPage ? routeNavigator.push("/") : routeNavigator.back());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [claiming, setClaiming] = useState({});
    const {showErrorSnackbar, showSuccessSnackbar} = useSnackbar();

    const loadMissions = () => {
        setLoading(true);
        fetchActiveMissions()
            .then(resp => {
                const payload = resp.data?.response || {daily: [], weekly: []};
                setMissions({
                    daily: (payload.daily || []).map(normalizeMission),
                    weekly: (payload.weekly || []).map(normalizeMission)
                });
                setError("");
            })
            .catch(err => {
                console.error("missions load error", err);
                setError("Failed to load missions");
                showErrorSnackbar("Failed to load missions");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadMissions();
        const poll = setInterval(loadMissions, 60000);
        return () => clearInterval(poll);
    }, []);

    useEffect(() => {
        const tick = setInterval(() => {
            setMissions(prev => ({
                daily: prev.daily.map(updateTime),
                weekly: prev.weekly.map(updateTime)
            }));
        }, 1000);
        return () => clearInterval(tick);
    }, []);

    const handleClaim = (missionId) => {
        setClaiming(prev => ({...prev, [missionId]: true}));

        claimMissionReward(missionId)
            .then(resp => {
                const payload = resp.data?.response || {coins: 0, playerHintBalances: {}};
                BonusStore.setCoins(payload.coins ?? BonusStore.coins);
                if (payload.playerHintBalances) {
                    GameStore.setPlayerHintBalances(payload.playerHintBalances);
                }
                setMissions(prev => ({
                    daily: prev.daily.map(m => m.id === missionId ? {...m, status: "claimed", progress: m.targetValue} : m),
                    weekly: prev.weekly.map(m => m.id === missionId ? {...m, status: "claimed", progress: m.targetValue} : m)
                }));
                showSuccessSnackbar("Mission reward claimed");

                // Показываем окно подписки после получения награды (если ещё не подписан и не показывали в сессии)
                if (sessionStorage.getItem(SUBSCRIPTION_MODAL_SHOWN_KEY) !== "1") {
                    getSubscriptionStatus().then(response => {
                        if (!response?.data?.response?.subscribed) {
                            sessionStorage.setItem(SUBSCRIPTION_MODAL_SHOWN_KEY, "1");
                            routeNavigator.showModal(SUBSCRIBE_MISSIONS_MODAL);
                        }
                    }).catch(err => {
                        console.error("Failed to check subscription status:", err);
                    });
                }
            })
            .catch(err => {
                console.error("mission claim error", err);
                showErrorSnackbar("Failed to claim reward");
            })
            .finally(() => {
                setClaiming(prev => ({...prev, [missionId]: false}));
            });
    };

    return (
        <>
            <BackgroundLetters/>

            <PanelHeader
                className="sticky top-0 z-20"
                before={<PanelHeaderBack className="[&>svg]:p-0" onClick={handleBackButton}/>}
                separator={null}
            >
                Missions
            </PanelHeader>

            {/* Контейнер повторяет ограничение ширины max-w-lg, как на других панелях */}
            <div className="relative z-10 flex flex-col p-4 items-center mx-auto w-full max-w-lg">
                <div className="w-full">
                    <MissionContent
                        missions={missions}
                        loading={loading}
                        error={error}
                        claiming={claiming}
                        onClaim={handleClaim}
                    />
                </div>
            </div>
        </>
    );
}

function MissionContent({missions, loading, error, claiming, onClaim}) {
    return (
        <div className="w-full bg-gray-800 text-gray-400 rounded-2xl shadow-md p-5 space-y-6">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-yellow-400 text-yellow-800 flex items-center justify-center">
                    <Icon28TargetOutline/>
                </div>
                <div className={"text-gray-400"}>
                    <p className="text-base font-semibold">Missions</p>
                    <p className="text-sm">Track progress for daily and weekly goals</p>
                </div>
            </div>

            <MissionColumn
                title="Daily missions"
                groupType="daily"
                missions={missions.daily}
                loading={loading}
                statusMessage={error && !missions.daily.length ? error : ""}
                onClaim={onClaim}
                claiming={claiming}
            />
            <MissionColumn
                title="Weekly missions"
                groupType="weekly"
                missions={missions.weekly}
                loading={loading}
                statusMessage={error && !missions.weekly.length ? error : ""}
                onClaim={onClaim}
                claiming={claiming}
            />
        </div>
    );
}

function MissionColumn({title, groupType, missions, loading, statusMessage, onClaim, claiming}) {
    const visibleMissions = missions.filter(m => m.status !== "expired");
    const groupTimeLeftSeconds = getGroupTimeLeftSeconds(visibleMissions);
    const groupBadgeClasses = getTypeBadgeClasses(groupType);
    // Поднимаем выполненные миссии вверх, чтобы акцентировать готовые награды
    const orderedMissions = [...visibleMissions].sort((a, b) => {
        const score = (status) => (status === "completed" ? 0 : status === "claimed" ? 2 : 1);
        return score(a.status) - score(b.status);
    });

    return (
        <div className="rounded-2xl border border-gray-700 bg-gray-700 p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <p className="text-sm font-semibold text-gray-300">{title}</p>
                    <p className="text-xs text-gray-400">
                        {visibleMissions.length
                            ? `${visibleMissions.length} ${visibleMissions.length === 1 ? "card" : "cards"}`
                            : "pending"}
                    </p>
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                    {visibleMissions.length > 0 && (
                        <span className={groupBadgeClasses}>
                            {formatTimeLeft(groupTimeLeftSeconds)}
                        </span>
                    )}
                </div>

            </div>

            {statusMessage && (
                <div className="text-xs text-red-500">{statusMessage}</div>
            )}

            {loading && visibleMissions.length === 0 ? (
                <div className="flex justify-center py-6">
                    <Spinner size="small"/>
                </div>
            ) : visibleMissions.length === 0 ? (
                <div className="rounded-xl border border-gray-600 bg-gray-800 p-4 text-xs text-gray-300">
                    No missions yet. Check back later.
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {orderedMissions.map(mission => (
                        <MissionCard
                            key={`${mission.id}-${mission.status}`}
                            mission={mission}
                            onClaim={onClaim}
                            claiming={Boolean(claiming[mission.id])}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function MissionCard({mission, onClaim, claiming}) {
    const progressPercent = mission.targetValue > 0 ? Math.min(100, Math.round((mission.progress / mission.targetValue) * 100)) : 0;
    const rewardParts = [];
    const progressText = getMissionProgressText(mission);
    const isCompleted = mission.status === "completed";
    const isClaimed = mission.status === "claimed";

    if (mission.rewardPreview?.coins) {
        rewardParts.push(`${mission.rewardPreview.coins} coins`);
    }

    if (mission.rewardPreview?.hint_tokens) {
        Object.entries(mission.rewardPreview.hint_tokens).forEach(([type, count]) => {
            const label = hintLabels[type] || type;
            rewardParts.push(`+${count} ${label}`);
        });
    }

    return (
        <div className={`rounded-2xl border p-4 flex flex-col gap-3 ${isCompleted ? "border-yellow-400/60 bg-yellow-400/5" : "border-gray-600"}`}>
            <div>
                <p className="text-base font-semibold text-gray-100">{mission.title}</p>
                <p className="text-xs text-gray-400">{mission.description}</p>
            </div>

            <div className="space-y-2">
                <div className="w-full h-2 rounded-full bg-gray-800">
                    <div
                        className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 transition-[width]"
                        style={{width: `${progressPercent}%`}}
                    />
                </div>
                <div className="flex justify-between text-xs">
                    <span className="text-gray-300">{progressText}</span>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 text-xs text-gray-400">
                {rewardParts.length > 0 ? rewardParts.map(part => (
                    <span key={part} className="px-2 py-1 rounded-full bg-gray-800/80">{part}</span>
                )) : <span className="text-gray-400">Reward unlocks after completion</span>}
                {isClaimed && (
                    <span className={BADGE_CLASSES}>
                        Reward claimed
                    </span>
                )}
            </div>

            {isClaimed ? null : isCompleted ? (
                <Button
                    size="s"
                    mode="primary"
                    stretched
                    className={BUTTON_CLASSES}
                    disabled={claiming}
                    onClick={() => onClaim(mission.id)}
                >
                    {claiming ? "Claiming..." : "Claim reward"}
                </Button>
            ) : (
                <div className="text-xs text-gray-400">
                    In progress - keep playing
                </div>
            )}
        </div>
    );
}
